import { Autocomplete, Box, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material'
import { FC, Fragment, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache, useGetCustomerLocalities } from 'src/api/localities'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { invalidateMainComponentCache, useBulkSendToLocality } from 'src/api/main-component'
import { invalidateMooringCache } from 'src/api/moorings'
import { useGetCagePositionsByLocality } from 'src/api/positions'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { AppTextField } from 'src/components/AppTextField'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'
import { MovementType } from 'src/enums'
import { ILocality, IMooring, IPosition } from 'src/generated-types'
import { ChangeLocationType } from 'src/generated-types/change-location-type'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { sortByName } from 'src/utils'
import { getMooringName, getPositionName, getUTCDate } from 'src/utils/function'
import { z } from 'zod'

export interface BulkChangeLocationDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  selectedEquipments: IMainComponent[]
  closeMainDialog?: () => void
  isChangeCageHidden?: boolean
  onSuccess?: () => void
}

const bulkChangeLocalitySchema = z.object({
  selectedLocalityId: z.number(),
})

const bulkChangePositionSchema = z.object({
  positionId: z.number(),
  positionName: z.string(),
  effectiveDate: z.date().or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v))),
})

const BulkChangeLocationDialog: FC<BulkChangeLocationDialogProps> = ({
  customerId,
  localityId,
  selectedEquipments,
  pop,
  closeMainDialog,
  isChangeCageHidden = false,
  onSuccess,
}) => {
  const { mutateAsync: bulkSendToLocality } = useBulkSendToLocality(customerId as EntityId)
  const { data: localities, isLoading } = useGetCustomerLocalities({ clientId: customerId as EntityId })
  const [formType, setFormType] = useState<ChangeLocationType>(ChangeLocationType.MoveToLocality)
  const [selectedMooringId, setSelectedMooringId] = useState<EntityId | undefined>()
  const [targetLocalityId, setTargetLocalityId] = useState<EntityId | undefined>()
  const { data: positionsData, isLoading: isLoadingPositions } = useGetCagePositionsByLocality(
    customerId,
    (targetLocalityId ?? localityId) as EntityId,
  )
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)

  const equipmentTypeIds = useMemo(
    () => Array.from(new Set(selectedEquipments.map(e => e.type?.id).filter(Boolean) as number[])),
    [selectedEquipments],
  )

  const { data: moveToLocalitySchemas, isLoading: isLoadingMoveToLocalitySchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds, movementTypes: [MovementType.SendToLocality] },
  )

  const { data: moveToPositionSchemas, isLoading: isLoadingMoveToPositionSchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds, movementTypes: [MovementType.ChangePosition] },
  )

  const { t } = useTranslation(['common', 'main-component'])
  const form = useZodForm(
    formType == ChangeLocationType.MoveToPosition ? bulkChangePositionSchema : bulkChangeLocalitySchema,
    {
      defaultValues: {
        selectedLocalityId: localityId,
      },
    },
  )

  const mooringList = useMemo(
    () =>
      (positionsData ?? []).reduce((moorings, currentValue) => {
        if (!moorings.some(mooring => mooring.id == currentValue.mooringId)) {
          moorings.push({
            id: currentValue.mooringId,
            name: currentValue.mooringName,
          } as IMooring)
        }
        return moorings
      }, [] as IMooring[]) as IMooring[],
    [positionsData],
  )

  const items = useMemo(
    () =>
      selectedEquipments
        .filter(e => e.id != null && e.selectedLocalityId != null)
        .map(e => ({ id: e.id as number, localityId: e.selectedLocalityId as number })),
    [selectedEquipments],
  )

  const invalidateOnSuccess = useCallback(
    (formValues: { selectedLocalityId?: number; positionId?: number }) => {
      invalidateMainComponentCache.invalidateAfterBulkAction(customerId as EntityId, selectedEquipments)
      const selLocId = formValues.selectedLocalityId ?? targetLocalityId
      if (selLocId) {
        invalidateLocalityCache.getCageStatus(customerId, selLocId)
        invalidateMainComponentCache.getMainComponents(
          customerId as EntityId,
          selLocId as EntityId,
          selectedMooringId as EntityId,
          formValues.positionId,
        )
        invalidateMainComponentCache.getOutgoingMainComponents(customerId, selLocId)
        if (formValues.positionId) {
          invalidateCertificateCache.getCertificatesByPosition(customerId, formValues.positionId)
        }
        invalidateMainComponentCache.invalidateEquipment(customerId)
        selectedEquipments.forEach(e => {
          invalidateMainComponentCache.getMainComponentById(customerId as EntityId, e.id as EntityId)
        })
        invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId)
        invalidateDeviation.getDeviations(customerId as EntityId)
        invalidateMooringCache.getMooringsForMap(customerId, localityId)
        invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
        invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
        invalidateCustomer.getCustomersActivityCounts(customerId)
        invalidateLocalityCache.getMaintenances(customerId, localityId)
        invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
        invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
        invalidateDocument.getDocuments(customerId)
        invalidateDocument.getDocumentsForApproval(customerId, localityId)
        invalidateLocalityCache.getCageStatus(customerId, localityId)
        invalidateLocalityCache.getLocality(customerId, localityId)
      }
    },
    [customerId, localityId, selectedEquipments, targetLocalityId, selectedMooringId],
  )

  const performBulkChangeLocation = async (
    payload: {
      items: Array<{ id: number; localityId: number; formSchemaReports?: IFormSchemaReport[] }>
      selectedLocalityId: number
      selectedPositionId?: number
      installationDate?: Date | string
      isForceAddedToLocality: boolean
    },
    equipmentReportsMap: Map<number, IFormSchemaReport[]>,
  ) => {
    const itemsWithReports = items.map(item => ({
      ...item,
      formSchemaReports: equipmentReportsMap.get(item.id) ?? [],
    }))

    await bulkSendToLocality(
      { ...payload, items: itemsWithReports },
      {
        onSuccess: () => {
          invalidateOnSuccess({
            selectedLocalityId: payload.selectedLocalityId,
            positionId: payload.selectedPositionId,
          })
          onSuccess?.()
          toast.success(t('locality-change-message', { ns: 'main-component' }))
          pop()
          closeMainDialog?.()
        },
        onError: error => {
          const errorData =
            (error as { response?: { data?: unknown }; data?: unknown })?.response?.data ??
            (error as { data?: unknown })?.data
          if (errorData === 'ExceedMaxCountForCage') {
            toast.error(t('exceedMaxCountForCage', { ns: 'main-component' }))
          } else {
            toast.error(t('updateFailed', { ns: 'common', defaultValue: 'Update failed' }))
          }
        },
      },
    )
  }

  const handleSubmit = useCallback(
    async (formValues: Record<string, unknown>) => {
      if (items.length === 0) return
      const payload =
        formType == ChangeLocationType.MoveToPosition
          ? {
              items,
              selectedLocalityId:
                (formValues.selectedLocalityId as number) ?? (selectedEquipments[0]?.selectedLocalityId as number),
              selectedPositionId: formValues.positionId as number,
              installationDate: formValues.effectiveDate as Date | string | undefined,
              isForceAddedToLocality: false,
            }
          : {
              items,
              selectedLocalityId: formValues.selectedLocalityId as number,
              isForceAddedToLocality: false,
            }

      const schemas = formType == ChangeLocationType.MoveToPosition ? moveToPositionSchemas : moveToLocalitySchemas
      const isLoadingSchemas =
        formType == ChangeLocationType.MoveToPosition ? isLoadingMoveToPositionSchemas : isLoadingMoveToLocalitySchemas

      await showChecklistsDialog({
        isBulk: true,
        schemas,
        isLoadingSchemas,
        selectedEquipments,
        maxCountValidation:
          formType == ChangeLocationType.MoveToPosition
            ? { positionId: payload.selectedPositionId, equipmentIds: items.map(item => item.id) }
            : undefined,
        onConfirm: reportsMap => performBulkChangeLocation(payload, reportsMap),
      })
    },
    [
      items,
      formType,
      selectedEquipments,
      showChecklistsDialog,
      moveToLocalitySchemas,
      moveToPositionSchemas,
      isLoadingMoveToLocalitySchemas,
      isLoadingMoveToPositionSchemas,
      customerId,
    ],
  )

  const disabledAction = useMemo(() => {
    if (formType == ChangeLocationType.MoveToPosition) {
      return !selectedMooringId
    }
    return false
  }, [formType, selectedMooringId])

  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      <GenericDialogLayout
        title={t('change-location', { ns: 'main-component' })}
        pop={pop}
        removedCloseButton={false}
        actions={[
          <FormSubmitButton
            disabled={disabledAction}
            key="send"
            createText={t('change-location', { ns: 'main-component' })}
          />,
        ]}
        topActions={[
          <Flex.Row key="top-action-list" width="100%" px={4}>
            <FormControl key="task-type-buttons-group">
              <RadioGroup
                value={formType}
                onChange={(_, v) => setFormType(v as unknown as ChangeLocationType)}
                row
                name="task-type-buttons-group"
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  key="move-to-locality"
                  value={ChangeLocationType.MoveToLocality}
                  control={<Radio />}
                  label={`${t('move-to-locality', { ns: 'main-component' })}`}
                />
                {!isChangeCageHidden && (
                  <FormControlLabel
                    key="move-to-position"
                    value={ChangeLocationType.MoveToPosition}
                    control={<Radio />}
                    label={`${t('move-to-position', { ns: 'main-component' })}`}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </Flex.Row>,
        ]}
      >
        {formType == ChangeLocationType.MoveToPosition && (
          <Fragment>
            <Autocomplete
              onChange={(_, value) => {
                setSelectedMooringId((value as IMooring)?.id)
              }}
              options={(mooringList?.sort(sortByName) || []) as IMooring[]}
              autoHighlight
              getOptionLabel={o => getMooringName(o.name)}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectMooring', { ns: 'main-component' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              onChange={(_, value) => {
                console.log('value', value)
                const pos = value as IPosition
                form.setValue('positionId', pos?.id, { shouldValidate: true })
                form.setValue('positionName', pos?.name ?? '', { shouldValidate: true })
              }}
              disabled={!selectedMooringId}
              options={
                (positionsData ?? [])
                  .filter(x => x.mooringId === selectedMooringId)
                  .sort((a, b) => parseInt(a.name) - parseInt(b.name)) as IPosition[]
              }
              autoHighlight
              getOptionLabel={o => getPositionName(o) as string}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {getPositionName(o) as string}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectCage', { ns: 'main-component' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Box py={1}>
              <BasicDatePicker label={t('effectiveDate', { ns: 'main-component' })} name="effectiveDate" />
            </Box>
          </Fragment>
        )}

        {formType == ChangeLocationType.MoveToLocality && (
          <Fragment>
            <Autocomplete
              onChange={(_, value) => {
                const loc = value as ILocality
                form.setValue('selectedLocalityId', loc.id)
                setTargetLocalityId(loc.id)
              }}
              options={(localities?.sort(sortByName) ?? []) as ILocality[]}
              autoHighlight
              getOptionLabel={o => o.name}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectALocality', { ns: 'service-station' })}
                  inputProps={{ ...params.inputProps }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Fragment>
        )}
      </GenericDialogLayout>
    </AppForm>
  )
}

export default BulkChangeLocationDialog
